import { collection, query, where, getDocs, deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import crudProfiler from '../../../pages/utils/CRUDPerformanceProfiler';

/**
 * Obtiene los datos de los formularios asociados al usuario actual.
 *
 * @async
 * @function
 * @param {Object} currentUser - Objeto que contiene la información del usuario actual.
 * @param {function} setServicioId - Función para establecer el ID del servicio.
 * @param {function} setLoading - Función para establecer el estado de carga.
 * @returns {Promise<Array<Object>>} - Una promesa que se resuelve con un array de objetos que representan los formularios.
 * @throws {Error} - Se lanza un error si hay problemas al obtener los datos.
 */
export const getFormsData = async (currentUser, setServicioId, setLoading) => {
    // 📊 Medir búsqueda de servicio
    crudProfiler.startMeasurement('68a_FetchUserService', {
        userEmail: currentUser.email
    });

    const serviciosRef = collection(db, 'servicios');
    const q = query(serviciosRef, where('admins', 'array-contains', currentUser.email));
    const querySnapshot = await getDocs(q);

    crudProfiler.endMeasurement('68a_FetchUserService', {
        servicesFound: querySnapshot.size
    });

    if (!querySnapshot.empty) {
        const primerServicio = querySnapshot.docs[0];

        setServicioId(primerServicio.id);

        // 📊 Medir carga de formularios
        crudProfiler.startMeasurement('68b_FetchFormsCollection', {
            serviceId: primerServicio.id
        });

        const documentsRef = collection(serviciosRef, primerServicio.id, 'forms');
        const formsSnapshot = await getDocs(documentsRef);

        crudProfiler.endMeasurement('68b_FetchFormsCollection', {
            formsFound: formsSnapshot.size
        });

        // 📊 Medir mapeo de datos
        crudProfiler.startMeasurement('68c_MapFormsData', {
            formsToMap: formsSnapshot.size
        });

        const formsData = formsSnapshot.docs.map((docSnap) => {
            const data = docSnap.data();
            const cliente = data.cliente ? data.cliente : [];
            return {
                ...data,
                id: docSnap.id,
                cliente,
            };
        });

        crudProfiler.endMeasurement('68c_MapFormsData', {
            formsMapped: formsData.length
        });

        setLoading(false);
        return formsData;
    }
    setLoading(false);
    return [];
};

/**
 * Elimina un formulario específico.
 *
 * @async
 * @function
 * @param {string} id - ID del formulario que se va a eliminar.
 * @param {string} servicioId - ID del servicio al que pertenece el formulario.
 * @param {function} setFormularios - Función para actualizar la lista de formularios después de la eliminación.
 * @returns {Promise<void>} - Una promesa que se resuelve cuando la eliminación es exitosa.
 * @throws {Error} - Se lanza un error si hay problemas al eliminar el formulario.
 */
export const deleteForm = async (id, servicioId, setFormularios) => {
    try {
        const confirmed = window.confirm('¿Estás seguro que quieres borrar el formulario?');
        if (!confirmed) {
            // Usuario canceló - no registrar como error
            crudProfiler.endMeasurement('71_DeleteFormRecord', {
                success: false,
                cancelled: true
            });
            crudProfiler.endSession({
                success: false,
                cancelled: true
            });
            return;
        }

        // 📊 Medir eliminación en Firestore
        crudProfiler.startMeasurement('71a_DeleteFromFirestore', {
            formId: id,
            serviceId: servicioId
        });

        console.log(id)
        const documentPath = `servicios/${servicioId}/forms/${id}`;
        const documentRef = doc(db, documentPath);

        await deleteDoc(documentRef);

        crudProfiler.endMeasurement('71a_DeleteFromFirestore', {
            success: true
        });

        // 📊 Medir actualización de estado
        crudProfiler.startMeasurement('71b_UpdateStateAfterDelete');

        setFormularios((prevFormularios) => prevFormularios.filter((formulario) => formulario.id !== id));

        crudProfiler.endMeasurement('71b_UpdateStateAfterDelete', {
            success: true
        });
    } catch (error) {
        console.error('Error al eliminar el formulario:', error);
        throw error; // Re-lanzar para que lo capture handleDelete
    }
};

/**
 * Genera un ID válido para Firestore a partir del nombre: espacios → _ y solo a-z0-9_
 */
const nameToId = (name) => {
    return String(name)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '') || 'formulario_copia';
};

/**
 * Duplica un formulario con un nuevo nombre. El ID del nuevo documento es el nombre con espacios en _.
 *
 * @async
 * @function
 * @param {string} formId - ID del formulario a duplicar.
 * @param {string} servicioId - ID del servicio.
 * @param {string} newName - Nombre para la copia.
 * @param {function} setFormularios - Función para actualizar la lista de formularios.
 * @returns {Promise<Object|null>} - El nuevo formulario creado o null si falla.
 */
export const duplicateForm = async (formId, servicioId, newName, setFormularios) => {
    if (!newName || !String(newName).trim()) {
        throw new Error('El nombre del formulario es obligatorio');
    }
    const trimmedName = String(newName).trim();
    const formsRef = collection(db, 'servicios', servicioId, 'forms');
    const formRef = doc(db, 'servicios', servicioId, 'forms', formId);
    const snap = await getDoc(formRef);
    if (!snap.exists()) {
        throw new Error('Formulario no encontrado');
    }
    const data = snap.data();
    const { id: _id, ...rest } = data;
    const newForm = { ...rest, name: trimmedName };

    let customId = nameToId(trimmedName);
    let candidateRef = doc(formsRef, customId);
    let n = 2;
    while ((await getDoc(candidateRef)).exists()) {
        customId = `${nameToId(trimmedName)}_${n}`;
        candidateRef = doc(formsRef, customId);
        n += 1;
    }

    await setDoc(candidateRef, { ...newForm, id: customId });
    const created = { ...newForm, id: customId };
    setFormularios((prev) => [...prev, created]);
    return created;
};
